import { type CellIndex, DIR_VEC, type Dir, indexToX, indexToY, manhattan } from './grid';
import type { Question } from './pack';
import { cursor, hashSeed, shuffle } from './rng';

/** 세션 덱 소비. 세션의 첫 3판이 24제를 무중복으로 정확히 소진한다. */
export function selectWords(
  ordered: readonly Question[],
  sessionSeed: string,
  runIndex: number,
  wordLenProfile: readonly number[],
): string[] {
  const buckets = new Map<number, Question[]>();
  for (const q of ordered) {
    const L = q.a.length;
    const b = buckets.get(L);
    if (b === undefined) buckets.set(L, [q]);
    else b.push(q);
  }

  const need = new Map<number, number>();
  for (const L of wordLenProfile) need.set(L, (need.get(L) ?? 0) + 1);

  const lengths = [...buckets.keys()].sort((a, b) => a - b);
  const cycle = runIndex % 3;
  for (const L of lengths) {
    const arr = buckets.get(L) as Question[];
    const stream = runIndex >= 3 ? `quiz#${L}#${runIndex}` : `quiz#${L}`;
    shuffle(cursor(hashSeed(sessionSeed, stream)), arr);
    if (runIndex >= 3) continue;
    const off = cycle * (need.get(L) ?? 0);
    if (off > 0 && arr.length > 0) {
      buckets.set(L, [...arr.slice(off), ...arr.slice(0, off)]);
    }
  }

  const taken = new Map<number, number>();
  const out: string[] = [];
  for (const L of wordLenProfile) {
    const arr = buckets.get(L);
    if (arr === undefined || arr.length === 0) {
      throw new Error(`selectWords: 길이 ${L} 문제가 팩에 없다`);
    }
    const i = taken.get(L) ?? 0;
    taken.set(L, i + 1);
    out.push((arr[i % arr.length] as Question).id);
  }
  return out;
}

/**
 * 역방향 보정 — 목표가 머리 '바로 뒤' 축 위에 있으면 +2.
 * 역방향 입력이 폐기되는 스네이크에서 정확한 하한은 d+2다.
 */
export function reverseFix(from: CellIndex, dir: Dir, to: CellIndex): number {
  const v = DIR_VEC[dir] as readonly [number, number];
  const dx = indexToX(to) - indexToX(from);
  const dy = indexToY(to) - indexToY(from);
  if (v[0] !== 0) return dy === 0 && dx * v[0] < 0 ? 2 : 0;
  return dx === 0 && dy * v[1] < 0 ? 2 : 0;
}

/** 우회율 분모. 세그먼트 0만 reverseFix 를 포함한다. */
export function segmentIdeals(
  head: CellIndex,
  dir: Dir,
  answerCells: readonly CellIndex[],
): number[] {
  const segs: number[] = [];
  let from = head;
  for (let k = 0; k < answerCells.length; k++) {
    const to = answerCells[k] as CellIndex;
    const base = manhattan(from, to);
    segs.push(k === 0 ? base + reverseFix(head, dir, to) : base);
    from = to;
  }
  return segs;
}

export function sumIdeal(segs: readonly number[]): number {
  let n = 0;
  for (const s of segs) n += s;
  return Math.max(1, n);
}
